import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { useParams } from 'react-router-dom';

import { getErrorMessage } from '~libs/axiosWrapper';

import { useCarePlanSelection } from '~clients/hooks/useCarePlanSelection';
import { useClientIdentity } from '~clients/hooks/useClientIdentity';
import { usePackageNames } from '~clients/hooks/usePackageNames';

import { ContainerHeader, ContainerPanel, ErrorResponse, Spinner } from '~components';
import { faUserNurse } from '~components/icon';

import { useGetCarePlanQuery, useGetCarePlansQuery } from '~clients/actions/carePlans';
import { getClientPackageSchedules } from '~main/actions/clients/clientPackageSchedules';

import { toggleQuickMenu } from '~main/reducers/app';
import { clearClientPackageSchedules } from '~main/reducers/clients/clientPackageSchedules';

import CarePlanFormModal from './CarePlanFormModal';
import CarePlanSidebar from './CarePlanSidebar';
import CarePlanTabs from './CarePlanTabs';

/**
 * Per-client care-plan view — split panel with the client's identity + plan list
 * in the sidebar and the selected plan's tab shell in the main panel. Tab content
 * is delivered by later stories. Serves two routes:
 *  - /clients/:client_id/care-plans — all care plans for the client
 *  - /clients/care-plans/:plan_id — a specific plan; the owning client is resolved
 *    from the plan's client_id FK and that plan is pre-selected
 */
const CarePlanView: React.FC = () => {
  const dispatch = useAppDispatch();
  const { client_id, plan_id } = useParams();

  // Deep link: resolve the owning client from the plan itself.
  const {
    data: linkedPlan,
    isLoading: isResolvingPlan,
    error: linkedPlanError,
  } = useGetCarePlanQuery(plan_id as string, { skip: plan_id === undefined });

  const clientId = plan_id !== undefined ? linkedPlan?.client_id : Number(client_id);
  const isValidClient = clientId !== undefined && Number.isInteger(clientId) && clientId > 0;
  const isInvalidClientRoute = plan_id === undefined && !isValidClient;

  const {
    data: plans,
    isLoading,
    error,
  } = useGetCarePlansQuery({ clientId: clientId as number }, { skip: !isValidClient });
  const { clientName } = useClientIdentity(isValidClient ? clientId : undefined);
  const { getPackageName } = usePackageNames();
  const { sortedPlans, selectedPlan, selectPlan, activeTab, onTabChange } = useCarePlanSelection(plans, plan_id);

  const loadError = linkedPlanError ?? error;

  useEffect(() => {
    dispatch(toggleQuickMenu(true));

    return () => {
      dispatch(toggleQuickMenu(false));
    };
  }, []);

  // Fetched at view level (not just the Overview tab) so the always-visible sidebar
  // checklist can resolve the linked schedule on any tab.
  useEffect(() => {
    if (!isValidClient) return undefined;
    dispatch(getClientPackageSchedules({ client_id: clientId as number }));

    return () => {
      dispatch(clearClientPackageSchedules());
    };
  }, [dispatch, clientId, isValidClient]);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const onNewCarePlan = () => setIsCreateOpen(true);

  return (
    <div className="p-2 care-plans-view">
      <ContainerHeader icon={faUserNurse} dataCy="care-plan-view-header">
        {clientName !== '' ? `Care Plans - ${clientName}` : 'Care Plans'}
      </ContainerHeader>
      {isInvalidClientRoute && <ErrorResponse dataCy="care-plan-invalid-client" message="Invalid client" />}
      {loadError !== undefined && <ErrorResponse dataCy="care-plan-load-error" message={getErrorMessage(loadError)} />}
      {isResolvingPlan && (
        <div className="care-plan-section-loading">
          <Spinner loading />
        </div>
      )}
      {isValidClient && loadError === undefined && (
        <>
          <ContainerPanel
            className="care-plans-panel"
            canCollapse={false}
            canResize={false}
            sidePanel={
              <CarePlanSidebar
                clientName={clientName}
                plans={sortedPlans}
                selectedPlanId={selectedPlan?.id}
                selectedPlan={selectedPlan}
                onSelectPlan={selectPlan}
                getPackageName={getPackageName}
                isLoading={isLoading}
                onNewCarePlan={onNewCarePlan}
              />
            }
          >
            {isLoading && (
              <div className="care-plan-section-loading">
                <Spinner loading />
              </div>
            )}
            {!isLoading && selectedPlan && (
              <CarePlanTabs plan={selectedPlan} activeTab={activeTab} onTabChange={onTabChange} />
            )}
            {!isLoading && !selectedPlan && (
              <p className="text-muted p-3" data-cy="care-plan-none-selected">
                No care plan selected.
              </p>
            )}
          </ContainerPanel>
          <CarePlanFormModal
            clientId={clientId as number}
            isOpen={isCreateOpen}
            setIsOpen={setIsCreateOpen}
            onCreated={selectPlan}
          />
        </>
      )}
    </div>
  );
};

export default CarePlanView;
